import React from 'react';
import { motion } from 'framer-motion';

/**
 * GuestMiniDashboard - Investment visibility for free account users
 *
 * Shows free account users what Mira has learned about them
 * to create investment visibility before hitting the paywall.
 *
 * Per GUEST_CONVERSION_UX_REDESIGN.md:
 * - Learning style progress bars
 * - Topics mastered
 * - Session count / streak
 * - "Unlock with subscription" teases
 *
 * Design: Compact, always visible, creates FOMO
 */

const GuestMiniDashboard = ({
  learningStyleWeights,
  sessionCount,
  streakDays,
  longestStreak,
  topicsCovered,
  totalMinutes,
  totalInteractions,
  miraOrbColor,
  onUpgradeClick
}) => {
  // Get top learning style
  const getTopLearningStyle = () => {
    if (!learningStyleWeights) return null;
    const entries = Object.entries(learningStyleWeights);
    if (entries.length === 0) return null;
    const sorted = entries.sort((a, b) => b[1] - a[1]);
    return sorted[0];
  };

  const topStyle = getTopLearningStyle();

  // Format learning style name
  const formatStyleName = (style) => {
    const names = {
      visual: 'Visual',
      auditory: 'Auditory',
      stepByStep: 'Step-by-step',
      conceptual: 'Conceptual',
      practical: 'Practical'
    };
    return names[style] || style;
  };

  // Get orb gradient
  const getOrbGradient = () => {
    const colors = {
      coral: 'linear-gradient(135deg, #FF6B6B, #FF8E8E)',
      ocean: 'linear-gradient(135deg, #4ECDC4, #6EE7DF)',
      sunset: 'linear-gradient(135deg, #FF9F43, #FFB76B)',
      midnight: 'linear-gradient(135deg, #5F27CD, #7C4DFF)',
      forest: 'linear-gradient(135deg, #26DE81, #4AE590)',
      lavender: 'linear-gradient(135deg, #A55EEA, #C084FC)',
      default: 'linear-gradient(135deg, #297A92, #42909E)'
    };
    return colors[miraOrbColor] || colors.default;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="guest-mini-dashboard"
      style={{
        background: 'linear-gradient(135deg, rgba(28, 28, 30, 0.95), rgba(44, 44, 46, 0.95))',
        backdropFilter: 'blur(12px)',
        borderRadius: '16px',
        padding: '16px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        maxWidth: '320px'
      }}
    >
      {/* Header with Mira orb */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '16px'
      }}>
        {/* Mini Mira orb */}
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: getOrbGradient(),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 4px 12px ${miraOrbColor ? 'rgba(0,0,0,0.3)' : 'rgba(41, 122, 146, 0.3)'}`
        }}>
          <div style={{ display: 'flex', gap: '8px', marginTop: '-4px' }}>
            <div style={{ width: '5px', height: '5px', backgroundColor: 'white', borderRadius: '50%' }} />
            <div style={{ width: '5px', height: '5px', backgroundColor: 'white', borderRadius: '50%' }} />
          </div>
        </div>

        <div>
          <h4 style={{
            margin: 0,
            fontSize: '14px',
            fontWeight: 600,
            color: '#ffffff'
          }}>
            Your Mira Profile
          </h4>
          <p style={{
            margin: '2px 0 0',
            fontSize: '12px',
            color: '#8e8e93'
          }}>
            What I've learned about you
          </p>
        </div>
      </div>

      {/* Stats Grid - 2x2 for accumulated investment visibility */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '8px',
        marginBottom: '14px'
      }}>
        {/* Sessions */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '10px',
          padding: '10px 12px'
        }}>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#ffffff' }}>
            {sessionCount || 0}
          </div>
          <div style={{ fontSize: '11px', color: '#8e8e93' }}>
            Sessions
          </div>
        </div>

        {/* Streak */}
        <div style={{
          background: streakDays > 0
            ? 'linear-gradient(135deg, rgba(255, 159, 67, 0.15), rgba(255, 183, 107, 0.15))'
            : 'rgba(255, 255, 255, 0.05)',
          borderRadius: '10px',
          padding: '10px 12px',
          border: streakDays > 0 ? '1px solid rgba(255, 159, 67, 0.3)' : 'none'
        }}>
          <div style={{
            fontSize: '20px',
            fontWeight: 700,
            color: streakDays > 0 ? '#FF9F43' : '#ffffff'
          }}>
            {streakDays || 0} {streakDays > 0 && '🔥'}
          </div>
          <div style={{ fontSize: '11px', color: '#8e8e93' }}>
            Day Streak {longestStreak > streakDays && `(Best: ${longestStreak})`}
          </div>
        </div>

        {/* Total Minutes - Investment visibility */}
        <div style={{
          background: totalMinutes > 30
            ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(139, 92, 246, 0.15))'
            : 'rgba(255, 255, 255, 0.05)',
          borderRadius: '10px',
          padding: '10px 12px',
          border: totalMinutes > 30 ? '1px solid rgba(139, 92, 246, 0.3)' : 'none'
        }}>
          <div style={{
            fontSize: '20px',
            fontWeight: 700,
            color: totalMinutes > 30 ? '#a78bfa' : '#ffffff'
          }}>
            {totalMinutes || 0}
          </div>
          <div style={{ fontSize: '11px', color: '#8e8e93' }}>
            Minutes Learning
          </div>
        </div>

        {/* Total Interactions - Investment visibility */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '10px',
          padding: '10px 12px'
        }}>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#ffffff' }}>
            {totalInteractions || 0}
          </div>
          <div style={{ fontSize: '11px', color: '#8e8e93' }}>
            Questions Asked
          </div>
        </div>
      </div>

      {/* Learning Style */}
      {topStyle && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '10px',
          padding: '12px',
          marginBottom: '14px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <span style={{ fontSize: '12px', color: '#8e8e93' }}>
              Learning Style
            </span>
            <span style={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#10b981'
            }}>
              {formatStyleName(topStyle[0])}
            </span>
          </div>
          {/* Progress bar */}
          <div style={{
            height: '6px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.min(topStyle[1], 100)}%` }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              style={{
                height: '100%',
                background: 'linear-gradient(90deg, #10b981, #34d399)',
                borderRadius: '3px'
              }}
            />
          </div>
        </div>
      )}

      {/* Topics covered */}
      {topicsCovered && topicsCovered.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <div style={{
            fontSize: '12px',
            color: '#8e8e93',
            marginBottom: '8px'
          }}>
            Topics Explored
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px'
          }}>
            {topicsCovered.slice(0, 4).map((topic, i) => (
              <span
                key={i}
                style={{
                  padding: '4px 10px',
                  background: 'rgba(99, 102, 241, 0.15)',
                  color: '#a5b4fc',
                  borderRadius: '100px',
                  fontSize: '11px',
                  fontWeight: 500
                }}
              >
                {topic}
              </span>
            ))}
            {topicsCovered.length > 4 && (
              <span style={{
                padding: '4px 10px',
                background: 'rgba(255, 255, 255, 0.05)',
                color: '#8e8e93',
                borderRadius: '100px',
                fontSize: '11px'
              }}>
                +{topicsCovered.length - 4} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Locked Features Teaser */}
      <div style={{
        background: 'rgba(139, 92, 246, 0.1)',
        borderRadius: '10px',
        padding: '12px',
        border: '1px solid rgba(139, 92, 246, 0.2)',
        marginBottom: '12px'
      }}>
        <div style={{
          fontSize: '12px',
          fontWeight: 600,
          color: '#a78bfa',
          marginBottom: '6px'
        }}>
          🔒 Unlock with Unlimited
        </div>
        <div style={{
          fontSize: '11px',
          color: '#8e8e93',
          lineHeight: 1.4
        }}>
          Parent progress reports, unlimited sessions, detailed analytics
        </div>
      </div>

      {/* Upgrade CTA */}
      <motion.button
        onClick={onUpgradeClick}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        style={{
          width: '100%',
          padding: '12px',
          background: 'linear-gradient(135deg, #297A92, #42909E)',
          color: '#ffffff',
          border: 'none',
          borderRadius: '10px',
          fontSize: '13px',
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
      >
        Keep Learning Forever
      </motion.button>
    </motion.div>
  );
};

export default GuestMiniDashboard;
